import type * as React from 'react';
import { Popover as BasePopover } from '@base-ui/react/popover';
import { X } from 'lucide-react';
import { cn } from '../../lib/cn.ts';

export const Popover = BasePopover.Root;

export type PopoverTriggerProps = Omit<BasePopover.Trigger.Props, 'style'>;

/*
 * Unstyled on purpose. A popover is opened by something that already has a look
 * — a Button, an IconButton — so the trigger merges into it with Base UI's
 * `render` rather than adding a second appearance of its own.
 */
export function PopoverTrigger(props: PopoverTriggerProps) {
  return <BasePopover.Trigger data-slot='popover-trigger' {...props} />;
}

export interface PopoverContentProps extends Omit<
  BasePopover.Popup.Props,
  'className' | 'style'
> {
  className?: string;
  side?: BasePopover.Positioner.Props['side'];
  align?: BasePopover.Positioner.Props['align'];
  sideOffset?: number;
  /*
   * What the panel hangs off, when that is not the trigger itself — a bar or a
   * pill the trigger is one button of, so the panel is centred on the whole
   * thing rather than on the button somebody happened to press.
   */
  anchor?: BasePopover.Positioner.Props['anchor'];
}

/**
 * A small panel hanging off what opened it.
 *
 * A dialog takes the screen; a popover stays next to its trigger, which is what
 * a handful of settings wants — the thing being changed stays in view. Outside
 * click, Escape and focus come from Base UI.
 */
export function PopoverContent({
  className,
  children,
  side = 'bottom',
  align = 'center',
  sideOffset = 8,
  anchor,
  ...props
}: PopoverContentProps) {
  return (
    <BasePopover.Portal>
      <BasePopover.Positioner
        side={side}
        align={align}
        sideOffset={sideOffset}
        anchor={anchor}
        className='z-50'
      >
        <BasePopover.Popup
          data-slot='popover-content'
          className={cn(
            /* Never wider than the window it hangs in, whatever width it is given. */
            'max-w-[calc(100vw-2rem)] rounded-xl border border-line bg-surface shadow-menu outline-none animate-pop',
            className,
          )}
          {...props}
        >
          {children}
        </BasePopover.Popup>
      </BasePopover.Positioner>
    </BasePopover.Portal>
  );
}

export type PopoverHeaderProps = Omit<React.ComponentProps<'div'>, 'style'>;

export function PopoverHeader({ className, ...props }: PopoverHeaderProps) {
  return (
    <div
      data-slot='popover-header'
      className={cn(
        'flex items-start justify-between gap-3 border-b border-fill px-4 pt-3.5 pb-2.5',
        className,
      )}
      {...props}
    />
  );
}

export interface PopoverTitleProps extends Omit<
  BasePopover.Title.Props,
  'className' | 'style'
> {
  className?: string;
}

export function PopoverTitle({ className, ...props }: PopoverTitleProps) {
  return (
    <BasePopover.Title
      data-slot='popover-title'
      className={cn('text-base font-bold text-ink', className)}
      {...props}
    />
  );
}

export interface PopoverDescriptionProps extends Omit<
  BasePopover.Description.Props,
  'className' | 'style'
> {
  className?: string;
}

export function PopoverDescription({ className, ...props }: PopoverDescriptionProps) {
  return (
    <BasePopover.Description
      data-slot='popover-description'
      className={cn('text-sm text-ink-muted', className)}
      {...props}
    />
  );
}

export interface PopoverCloseProps extends Omit<
  BasePopover.Close.Props,
  'className' | 'style' | 'children'
> {
  className?: string;
}

export function PopoverClose({
  className,
  'aria-label': ariaLabel = 'Close',
  ...props
}: PopoverCloseProps) {
  return (
    <BasePopover.Close
      data-slot='popover-close'
      aria-label={ariaLabel}
      className={cn(
        'flex size-6.5 shrink-0 cursor-pointer items-center justify-center rounded-full text-ink-muted transition-colors hover:bg-fill hover:text-ink focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-ink-faint',
        className,
      )}
      {...props}
    >
      <X className='size-3.5' aria-hidden='true' />
    </BasePopover.Close>
  );
}
